import { onBeforeUnmount, onMounted, nextTick } from 'vue';
import getEmbeddedInModule from '@/helpers/getEmbeddedInModule';
import InternalModules from '@/models/InternalModules';
import { createEmbeddedHeightReporter } from './embeddedHeightReporter';

/**
 * Reports the app's full content height to the embedding host so it can size the
 * iframe to fit — eliminating an inner scrollbar inside the iframe.
 *
 * Boundary: only the SIU host (friss-facts incident page) embeds this app in a
 * fixed-height iframe without resize logic of its own. The FSC casemanagement
 * host already sizes its iframe, so this is gated to SIU only. The message shape
 * `{ type: 'resize', height }` matches the contract the SIU host listens for.
 */
export const useEmbeddedHostResize = (): void => {
  const shouldReportHeight = getEmbeddedInModule() === InternalModules.Siu;

  let observer: ResizeObserver | null = null;
  const { postHeight } = createEmbeddedHeightReporter();

  onMounted(() => {
    if (!shouldReportHeight) {
      return;
    }

    // Why: post once after the first paint so the host sizes the iframe to the
    // initial content, then keep it in sync as tabs mount/unmount and async data
    // grows or shrinks the page.
    nextTick(postHeight);

    // Edge case: ResizeObserver is absent in jsdom — guard so unit tests that
    // mount App.vue without a mock do not throw (mirrors TruncatedTextTooltip).
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => postHeight());
      observer.observe(document.body);
    }

    window.addEventListener('resize', postHeight);
  });

  onBeforeUnmount(() => {
    if (!shouldReportHeight) {
      return;
    }

    observer?.disconnect();
    observer = null;
    window.removeEventListener('resize', postHeight);
  });
};
